/**
 * Positive Feedback Page — Dignidash Layout Match
 * 
 * Exact match of the old dignidash positive feedback page:
 * - Star icon + Dealer Name header
 * - CUSTOMER NAME (uppercase) + Date
 * - Customer quote in italics
 * - "Why this matters:" explanation
 * - "Feedback at [timestamp]" marker
 * - Audio waveform visualizer with playback
 * - "Share with Marketing" + "Export Clip" buttons
 * - Two-column grid layout
 */

import { useState, useMemo } from 'react';
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Star, 
  Loader2, 
  Download,
  Share2,
  Trophy,
  RefreshCw,
  MessageSquare,
  ThumbsUp,
  Smile,
} from "lucide-react";
import { useOrganizationContext } from "@/hooks/useOrganization";
import { trpc } from "@/lib/trpc";
import AudioPlayer from "@/components/AudioPlayer";
import { toast } from "sonner";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

const COLORS = {
  positive: "#22c55e",
  negative: "#ef4444",
  neutral: "#6b7280",
};

// ============================================
// Parse "M:SS" duration string to seconds
// ============================================
function parseDurationToSeconds(duration: string | null | undefined): number {
  if (!duration) return 0;
  const parts = String(duration).split(':');
  if (parts.length === 2) {
    return parseInt(parts[0]) * 60 + parseInt(parts[1]);
  }
  if (parts.length === 3) {
    return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
  }
  return parseFloat(duration) || 0;
}

// ============================================
// Format seconds to M:SS
// ============================================
function formatTimestamp(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// ============================================
// Estimate feedback timestamp (70% of call duration as heuristic)
// ============================================
function estimateFeedbackTimestamp(durationStr: string | null | undefined): number | null {
  const totalSeconds = parseDurationToSeconds(durationStr);
  if (totalSeconds <= 0) return null;
  // Feedback typically happens in the latter part of the call
  return Math.floor(totalSeconds * 0.7);
}

// ============================================
// Testimonial Card — Matches old dignidash exactly
// ============================================
function TestimonialCard({ feedback, onShare, onExport }: {
  feedback: any;
  onShare: () => void;
  onExport: () => void;
}) {
  const dealerName = feedback.dealer || 'Unknown Dealer';
  const customerName = feedback.full_name || 
    (feedback.first_name && feedback.last_name 
      ? `${feedback.first_name} ${feedback.last_name}` 
      : null);
  const callDate = feedback.call_time 
    ? new Date(feedback.call_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'N/A';
  const quote = feedback.dealership_feedback || null;
  const whyItMatters = feedback.call_summary || feedback.detailed_call_summary || null;
  const recordingUrl = feedback.recording_url || null;
  const feedbackTimestamp = estimateFeedbackTimestamp(feedback.call_duration);

  return (
    <Card className="overflow-hidden border-l-4 border-l-green-500 bg-card">
      <CardContent className="p-5">
        {/* Header: Star + Dealer Name + Share icon */}
        <div className="flex items-start justify-between mb-1">
          <div className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500 fill-yellow-500 flex-shrink-0" />
            <span className="font-semibold text-base">{dealerName}</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            onClick={onShare}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Customer name + Date */}
        <div className="flex items-center gap-2 mb-4 ml-7">
          {customerName && (
            <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
              {customerName}
            </span>
          )}
          {customerName && <span className="text-xs text-muted-foreground">•</span>}
          <span className="text-xs text-muted-foreground">{callDate}</span>
        </div>

        {/* Customer quote */}
        {quote && (
          <blockquote className="mb-4 pl-4 border-l-2 border-muted italic text-sm text-muted-foreground leading-relaxed">
            "{quote}"
          </blockquote>
        )}

        {/* Why this matters */}
        {whyItMatters && (
          <div className="text-sm mb-4">
            <span className="font-medium text-foreground">Why this matters: </span>
            <span className="text-muted-foreground">{whyItMatters}</span>
          </div>
        )}

        {/* Feedback timestamp marker */}
        {feedbackTimestamp !== null && (
          <div className="text-xs text-muted-foreground mb-2">
            Feedback at {formatTimestamp(feedbackTimestamp)}
          </div>
        )}

        {/* Audio waveform + player */}
        {recordingUrl && (
          <div className="mb-4 rounded-lg bg-muted/30 p-3">
            <AudioPlayer
              audioUrl={recordingUrl}
              highlightTimestamp={feedbackTimestamp || undefined}
            />
          </div>
        )}

        {/* Action buttons: Share with Marketing + Export Clip */}
        <div className="flex items-center justify-between pt-2 border-t border-border">
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs text-muted-foreground hover:text-foreground gap-1.5"
            onClick={onShare}
          >
            <Share2 className="h-3.5 w-3.5" />
            Share with Marketing
          </Button>
          {recordingUrl && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-xs text-muted-foreground hover:text-foreground gap-1.5"
              onClick={onExport}
            >
              <Download className="h-3.5 w-3.5" />
              Export Clip
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================
// Main Component
// ============================================
export default function PositiveFeedback() {
  const { organizationName, isLoading: orgLoading } = useOrganizationContext();
  
  const { data: legacyFeedback, isLoading: legacyLoading, refetch } = trpc.dignifi.positiveFeedback.useQuery();
  const { data: complianceSummary, isLoading: complianceLoading } = trpc.dignifi.complianceSummary.useQuery();

  const isLoading = orgLoading || legacyLoading || complianceLoading;

  // Filter to only feedback with actual content (quote or recording)
  const feedbackWithContent = useMemo(() => {
    if (!legacyFeedback) return [];
    return legacyFeedback.filter((f: any) => {
      const hasQuote = f.dealership_feedback && 
        f.dealership_feedback.trim() !== '' && 
        f.dealership_feedback !== 'nan';
      const hasRecording = f.recording_url && f.recording_url.trim() !== '';
      return hasQuote || hasRecording;
    });
  }, [legacyFeedback]);

  // Feedback with recordings (for the main testimonials section)
  const feedbackWithRecordings = useMemo(() => {
    return feedbackWithContent.filter((f: any) => 
      f.recording_url && f.recording_url.trim() !== ''
    );
  }, [feedbackWithContent]);

  // Dealer leaderboard
  const dealerStats = useMemo(() => {
    const grouped = feedbackWithContent.reduce((acc: Record<string, { dealer: string; count: number }>, item: any) => {
      const dealer = item.dealer || 'Unknown Dealer';
      if (!acc[dealer]) {
        acc[dealer] = { dealer, count: 0 };
      }
      acc[dealer].count++;
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => b.count - a.count);
  }, [feedbackWithContent]);

  // Share handler
  const shareWithMarketing = (item: any) => {
    const customerName = item.full_name || 
      (item.first_name && item.last_name ? `${item.first_name} ${item.last_name}` : 'Customer');
    const message = [
      `⭐ Positive Feedback — ${item.dealer || 'Unknown Dealer'}`,
      `Customer: ${customerName}`,
      `Date: ${item.call_time ? new Date(item.call_time).toLocaleDateString() : 'N/A'}`,
      '',
      `Testimonial:`,
      `"${item.dealership_feedback || ''}"`,
      '',
      item.call_summary || item.detailed_call_summary ? `Context: ${item.call_summary || item.detailed_call_summary}` : '',
    ].filter(Boolean).join('\n');
    navigator.clipboard.writeText(message);
    toast.success('Copied to clipboard! Ready to share with marketing team.');
  };

  // Export clip handler
  const exportClip = (item: any) => {
    if (!item.recording_url) {
      toast.error('No recording available for this call.');
      return;
    }
    // Download the recording
    const a = document.createElement('a');
    a.href = item.recording_url;
    const customerName = item.full_name || 
      (item.first_name && item.last_name ? `${item.first_name}_${item.last_name}` : 'customer');
    a.download = `positive-feedback-${customerName.replace(/\s+/g, '_')}-${item.call_time ? new Date(item.call_time).toISOString().split('T')[0] : 'unknown'}.wav`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success('Downloading recording...');
  };

  // Export all to CSV
  const exportToCSV = () => {
    if (feedbackWithContent.length === 0) return;

    const headers = ['Date', 'Dealer', 'Customer', 'Testimonial', 'Context', 'Recording URL'];
    const rows = feedbackWithContent.map((f: any) => [
      f.call_time ? new Date(f.call_time).toLocaleDateString() : 'N/A',
      f.dealer || 'Unknown',
      f.full_name || (f.first_name && f.last_name ? `${f.first_name} ${f.last_name}` : ''),
      f.dealership_feedback || '',
      f.call_summary || f.detailed_call_summary || '',
      f.recording_url || '',
    ]);
    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `positive-feedback-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exported successfully!');
  };

  // Loading
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-muted-foreground">Loading feedback data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const positiveCalls = Number(complianceSummary?.positive_calls || 0);
  const negativeCalls = Number(complianceSummary?.negative_calls || 0);
  const totalCalls = Number(complianceSummary?.total_calls || 0);
  const neutralCalls = totalCalls - positiveCalls - negativeCalls;
  const positiveRate = totalCalls > 0 ? ((positiveCalls / totalCalls) * 100).toFixed(1) : '0';

  const sentimentData = [
    { name: 'Positive', value: positiveCalls, color: COLORS.positive },
    { name: 'Negative', value: negativeCalls, color: COLORS.negative },
    { name: 'Neutral', value: neutralCalls, color: COLORS.neutral },
  ].filter(d => d.value > 0);

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* ============================================ */}
        {/* Header */}
        {/* ============================================ */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Star className="h-8 w-8 text-yellow-500 fill-yellow-500" />
              Positive Feedback & Testimonials
            </h1>
            <p className="text-muted-foreground mt-1">
              Customer testimonials and great interactions for marketing and recognition
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportToCSV} className="gap-2">
              <Download className="h-4 w-4" />
              Export All
            </Button>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* ============================================ */}
        {/* Key Metrics */}
        {/* ============================================ */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Positive Calls</CardTitle>
              <ThumbsUp className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">{positiveCalls.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">{positiveRate}% of all calls</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">With Recordings</CardTitle>
              <MessageSquare className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{feedbackWithRecordings.length}</div>
              <p className="text-xs text-muted-foreground">Playable audio clips</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Satisfaction Rate</CardTitle>
              <Star className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-yellow-500">{positiveRate}%</div>
              <p className="text-xs text-muted-foreground">Positive sentiment</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Top Performer</CardTitle>
              <Trophy className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold truncate">
                {dealerStats.length > 0 ? dealerStats[0].dealer : 'N/A'}
              </div>
              <p className="text-xs text-muted-foreground">
                {dealerStats.length > 0 ? `${dealerStats[0].count} testimonials` : 'No data'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* ============================================ */}
        {/* Dealer Leaderboard + Sentiment */}
        {/* ============================================ */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Dealer Leaderboard */}
          {dealerStats.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  Dealer Leaderboard
                </CardTitle>
                <CardDescription>Dealers ranked by positive customer feedback</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {dealerStats.slice(0, 10).map((stat, index) => (
                    <div
                      key={stat.dealer}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <Badge
                          variant={index === 0 ? 'default' : 'secondary'}
                          className={`w-8 h-8 flex items-center justify-center ${index === 0 ? 'bg-yellow-500 hover:bg-yellow-600' : ''}`}
                        >
                          {index + 1}
                        </Badge>
                        <span className="font-medium truncate">{stat.dealer}</span>
                      </div>
                      <Badge variant="outline" className="text-green-500 border-green-500/50">
                        {stat.count} testimonial{stat.count !== 1 ? 's' : ''}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Sentiment Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smile className="h-5 w-5" />
                Sentiment Distribution
              </CardTitle>
              <CardDescription>Customer sentiment across all calls</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sentimentData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {sentimentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => [value.toLocaleString(), "Calls"]}
                      contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Sentiment bars */}
              <div className="space-y-3 mt-4">
                <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <ThumbsUp className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium">Positive</span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-green-500">{positiveCalls.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground ml-2">{positiveRate}%</span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Neutral</span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold">{neutralCalls.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      {totalCalls > 0 ? ((neutralCalls / totalCalls) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <ThumbsUp className="h-4 w-4 text-red-500 rotate-180" />
                    <span className="text-sm font-medium">Negative</span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-red-500">{negativeCalls.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      {totalCalls > 0 ? ((negativeCalls / totalCalls) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ============================================ */}
        {/* Customer Testimonials — Dignidash Layout */}
        {/* Two-column grid, each card with audio waveform */}
        {/* ============================================ */}
        <div>
          <h2 className="text-xl font-bold mb-4">Customer Testimonials</h2>
          
          {feedbackWithRecordings.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {feedbackWithRecordings.map((feedback: any, index: number) => (
                <TestimonialCard
                  key={feedback.call_id || index}
                  feedback={feedback}
                  onShare={() => shareWithMarketing(feedback)}
                  onExport={() => exportClip(feedback)}
                />
              ))}
            </div>
          ) : feedbackWithContent.length > 0 ? (
            <div className="grid gap-4 md:grid-cols-2">
              {feedbackWithContent.slice(0, 20).map((feedback: any, index: number) => (
                <TestimonialCard
                  key={feedback.call_id || index}
                  feedback={feedback}
                  onShare={() => shareWithMarketing(feedback)}
                  onExport={() => exportClip(feedback)}
                />
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No testimonials found yet.</p>
                  <p className="text-sm mt-1">Positive feedback will appear here as calls are processed.</p>
                </div>
              </CardContent>
            </Card>
          )}

          {feedbackWithRecordings.length > 0 && feedbackWithRecordings.length < feedbackWithContent.length && (
            <p className="text-sm text-muted-foreground mt-4 text-center">
              Showing {feedbackWithRecordings.length} testimonials with recordings 
              ({feedbackWithContent.length} total with feedback)
            </p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
